import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import {
    CreateNewTaskRequest,
    CreateNewTaskResponse,
    DeleteTasksRequest,
    MigrateTaskData2023Request,
    RemoveTaskDueDateRequest,
    RemoveTaskDurationRequest,
    RemoveTaskScheduleDateRequest,
    RemoveTaskScheduleTimeRequest,
    UpdateTaskDescriptionRequest,
    UpdateTaskDueDateRequest,
    UpdateTaskDurationRequest,
    UpdateTaskForDndRequest,
    UpdateTaskProjectRequest,
    UpdateTaskScheduleDateRequest,
    UpdateTaskScheduleTimeRequest,
    UpdateTaskStatusRequest,
    UpdateTaskTagRequest,
    UpdateTaskTimeSpentRequest,
    UpdateTaskTitleRequest,
} from "@lifeat/shared/task-management/TaskManagementFunctionContracts";
import { userTaskDataCollection } from "@lifeat/shared/task-management/TaskManagementTypes";
import {
    isValidTaskDuration,
    isValidTaskScheduleTime,
    isValidTimeSpent,
} from "@lifeat/shared/task-management/TaskManagementUtil";
import {
    ReorderTaskRequest,
    ReorderTaskResponse,
} from "@lifeat/shared/task-management/TaskReorderingFunctionContracts";
import { TaskStatus } from "@lifeat/shared/task-management/TaskStatus";
import { isValidISODateString } from "@lifeat/shared/util/StringUtil";
import { logError } from "@service/ConsoleLoggerService";
import { FirestoreService } from "@service/FirestoreService";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { ClientTaskItemType } from "./TaskManagementTypes";

/**
 * Service for Task Management related operations.
 */
export class TaskManagementService {
    static async migrateTaskData2023(timezone: string): Promise<void> {
        try {
            const payload: MigrateTaskData2023Request = { timezone };
            await callMonolith("migrate-task-data-2023", payload);
        } catch (err) {
            alert(
                `There was an issue migrating your task data. Please contact LifeAt support ` +
                    `by going to "https://lifeat.io/faq".\n\nError: ${getErrorMessage(err)}`,
            );
            logError(`Error migrating task data: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Makes a request to the serverless function to create a new task.
     * @returns {Promise<CreateNewTaskResponse>} Response containing the
     *  task ID and task item.
     */
    static async createNewTask(payload: CreateNewTaskRequest): Promise<CreateNewTaskResponse | null> {
        try {
            return await callMonolith("create-new-task", payload);
        } catch (err) {
            console.log(`Error creating task: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the title of the
     * task with the given ID.
     * @param taskId ID of the task to update.
     * @param title New title of the task.
     */
    static async updateTaskTitle(taskId: string, title: string): Promise<void> {
        try {
            const payload: UpdateTaskTitleRequest = {
                taskId: taskId,
                title: title,
            };
            await callMonolith("update-task-title", payload);
        } catch (err) {
            console.log(`Error updating task title: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the description of
     * the task with the given ID.
     * @param taskId ID of the task to update.
     * @param description New description of the task.
     */
    static async updateTaskDescription(taskId: string, description: string): Promise<void> {
        try {
            const payload: UpdateTaskDescriptionRequest = {
                taskId: taskId,
                description: description,
            };
            await callMonolith("update-task-description", payload);
        } catch (err) {
            console.log(`Error updating task description: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the status of the
     * task with the given ID.
     * @param taskId ID of the task to update.
     * @param status New status of the task.
     */
    static async updateTaskStatus(taskId: string, status: TaskStatus): Promise<void> {
        try {
            const payload: UpdateTaskStatusRequest = {
                taskId: taskId,
                status: status,
            };
            await callMonolith("update-task-status", payload);
        } catch (err) {
            console.log(`Error updating task status: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the tag of the
     * task with the given ID.
     * @param taskId ID of the task to update.
     * @param tagId ID of the tag to assign to the task. If not provided, task will have its tag removed.
     */
    static async updateTaskTag(taskId: string, tagId: string | null): Promise<void> {
        try {
            const payload: UpdateTaskTagRequest = {
                taskId: taskId,
                tagId: tagId,
            };
            await callMonolith("update-task-tag", payload);
        } catch (err) {
            console.log(`Error updating task tag: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the project of the
     * task with the given ID.
     * @param taskId ID of the task to update.
     * @param projectId ID of the project to assign to the task. If not provided, task will have its project removed.
     */
    static async updateTaskProject(taskId: string, projectId: string | null): Promise<void> {
        try {
            const payload: UpdateTaskProjectRequest = {
                taskId: taskId,
                projectId: projectId,
            };
            await callMonolith("update-task-project", payload);
        } catch (err) {
            console.log(`Error updating task project: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to delete a list of tasks.
     * @param taskIds IDs of the tasks to delete.
     */
    static async deleteTasks(taskIds: string[]): Promise<void> {
        try {
            const payload: DeleteTasksRequest = {
                taskIds: taskIds,
            };
            await callMonolith("delete-tasks", payload);
        } catch (err) {
            console.log(`Error deleting tasks: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the duration of the task.
     */
    static async updateTaskDuration(taskId: string, duration: number): Promise<void> {
        try {
            if (!isValidTaskDuration(duration)) {
                throw new Error(`Invalid duration: ${duration}`);
            }
            const payload: UpdateTaskDurationRequest = {
                taskId,
                durationMins: duration,
            };
            await callMonolith("update-task-duration", payload);
        } catch (err) {
            console.log(`Error updating task duration: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the schedule time of the task.
     * @param taskId ID of the task to update.
     * @param scheduleDate date string in the format yyyy-MM-dd
     */
    static async updateTaskScheduleDate(taskId: string, scheduleDate: string): Promise<void> {
        try {
            if (!isValidISODateString(scheduleDate)) {
                throw new Error(`Invalid schedule date: ${scheduleDate}`);
            }
            const payload: UpdateTaskScheduleDateRequest = {
                taskId,
                scheduleDate,
            };
            await callMonolith("update-task-schedule-date", payload);
        } catch (err) {
            console.log(`Error updating task schedule date: ${err}`);
            throw err;
        }
    }

    static async updateTaskScheduleDateAndOrderRank(
        taskId: string,
        scheduleDate: string,
        lowerBoundTaskId?: string,
        upperBoundTaskId?: string,
    ): Promise<void> {
        if (!isValidISODateString(scheduleDate)) {
            throw new Error(`Invalid schedule date: ${scheduleDate}`);
        }
        try {
            const payload: UpdateTaskForDndRequest = {
                taskId,
                scheduleDate,
                lowerBoundTaskId,
                upperBoundTaskId,
            };
            await callMonolith("update-task-for-dnd", payload);
        } catch (err) {
            console.log(`Error updating task schedule date: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the schedule time of the task.
     * @param taskId ID of the task to update.
     * @param scheduleTime time string in the format HH:mm
     */
    static async updateTaskScheduleTime(taskId: string, scheduleTime: string): Promise<void> {
        try {
            if (!isValidTaskScheduleTime(scheduleTime)) {
                throw new Error(`Invalid schedule time: ${scheduleTime}`);
            }
            const payload: UpdateTaskScheduleTimeRequest = {
                taskId,
                scheduleTime,
            };
            await callMonolith("update-task-schedule-time", payload);
        } catch (err) {
            console.log(`Error updating task schedule time: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the due date of the task.
     * @param taskId ID of the task to update.
     * @param dueDate date string in the format yyyy-MM-dd
     */
    static async updateTaskDueDate(taskId: string, dueDate: string): Promise<void> {
        try {
            const payload: UpdateTaskDueDateRequest = {
                taskId,
                dueDate: dueDate,
            };
            await callMonolith("update-task-due-date", payload);
        } catch (err) {
            console.log(`Error updating task due date: ${err}`);
            throw err;
        }
    }

    static async removeTaskDueDate(taskId: string): Promise<void> {
        const payload: RemoveTaskDueDateRequest = { taskId };
        try {
            await callMonolith("remove-task-due-date", payload);
        } catch (err) {
            console.log(`Error removing task due date: ${err}`);
            throw err;
        }
    }

    static async removeTaskDuration(taskId: string): Promise<void> {
        const payload: RemoveTaskDurationRequest = { taskId };
        try {
            await callMonolith("remove-task-duration", payload);
        } catch (err) {
            console.log(`Error removing task duration: ${err}`);
            throw err;
        }
    }

    static async removeTaskScheduleDate(taskId: string): Promise<void> {
        const payload: RemoveTaskScheduleDateRequest = { taskId };
        try {
            await callMonolith("remove-task-schedule-date", payload);
        } catch (err) {
            console.log(`Error removing task schedule date: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to remove the schedule time of the task.
     */
    static async removeTaskScheduleTime(taskId: string, timezone: string): Promise<void> {
        const payload: RemoveTaskScheduleTimeRequest = {
            taskId,
            clientTimezone: timezone,
        };
        try {
            await callMonolith("remove-task-schedule-time", payload);
        } catch (err) {
            console.log(`Error removing task schedule time: ${err}`);
            throw err;
        }
    }

    /**
     * Makes a request to the serverless function to update the time spent of
     * the task with the given ID.
     * @param taskId ID of the task to update.
     * @param timeSpentMins Updated time spent value of the task.
     */
    static async updateTaskTimeSpent(taskId: string, timeSpentMins: number): Promise<void> {
        try {
            if (!isValidTimeSpent(timeSpentMins)) {
                throw new Error(`Invalid time spent: ${timeSpentMins}`);
            }
            const payload: UpdateTaskTimeSpentRequest = {
                taskId: taskId,
                timeSpentMins: timeSpentMins,
            };
            await callMonolith("update-task-time-spent", payload);
        } catch (err) {
            console.log(`Error updating task time spent: ${err}`);
            throw err;
        }
    }

    static async reorderTask(
        targetTaskId: string,
        lowerBoundTaskId?: string,
        upperBoundTaskId?: string,
    ): Promise<ReorderTaskResponse | null> {
        const payload: ReorderTaskRequest = {
            targetTaskId: targetTaskId,
            lowerBoundId: lowerBoundTaskId,
            upperBoundId: upperBoundTaskId,
        };
        return callMonolith("reorder-task", payload).catch(() => null);
    }

    /**
     * Fetch a task from the firestore database
     */
    static async getTaskItem(uid: string, taskId: string): Promise<ClientTaskItemType | null> {
        const fs = FirestoreService();
        const task = await fs.getDocument(userTaskDataCollection.at(uid).at("tasks").at(taskId).node);

        return task ? ({ ...task, isDraft: false } as ClientTaskItemType) : null;
    }
}
